import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Divider,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TableHead,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GavelIcon from "@mui/icons-material/Gavel";
import LockIcon from "@mui/icons-material/Lock";
import { normalizeTelefonoForStorage } from "../utils/telefonoCordoba";
import { revelarClaves } from "../services/clienteService";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ClienteImpresion = ({ cliente, contactos = [], onVolver, clavesReales }) => {
    const [clavesExportar, setClavesExportar] = useState(clavesReales);
    const [dialogoSeguridad, setDialogoSeguridad] = useState(false);
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [errorPassword, setErrorPassword] = useState('');
    const [generarPendiente, setGenerarPendiente] = useState(false);

    useEffect(() => {
        if (generarPendiente && clavesExportar) {
            setGenerarPendiente(false);
            setTimeout(() => {
                ejecutarGeneracionPDF();
            }, 500); // Esperar a que React renderice las claves en el DOM
        }
    }, [clavesExportar, generarPendiente]);

    if (!cliente) return null;

    const primaryBlue = "#3b82f6";

    const formatearFecha = (fecha) => {
        if (!fecha) return "--";
        const partes = fecha.split("-");
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
    };

    const formatearCuil = (cuil) => {
        if (!cuil) return "--";
        const s = cuil.toString().replace(/-/g, "");
        if (s.length !== 11) return cuil;
        return `${s.slice(0, 2)}-${s.slice(2, 10)}-${s.slice(10)}`;
    };

    // Función para exportar a PDF utilizando html2canvas (como en ReportesGastos)
    const ejecutarGeneracionPDF = async () => {
        const printElement = document.getElementById("vista-impresion");
        if (!printElement) return;

        try {
            // Guardamos estilos originales si los hubiera
            const originalBorder = printElement.style.border;
            const originalShadow = printElement.style.boxShadow;

            // Ajustamos temporalmente el contenedor para asegurar que html2canvas pinte bien
            printElement.style.border = "none";
            printElement.style.boxShadow = "none";

            // Tomamos la captura del div (esto se procesa en el fondo)
            const canvas = await html2canvas(printElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff"
            });

            // Restauramos los estilos visuales originales
            printElement.style.border = originalBorder;
            printElement.style.boxShadow = originalShadow;

            // Instanciamos el PDF, tamaño A4 en milímetros
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            // Obtenemos alto y ancho internos de A4
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Extraer la imagen
            const imgData = canvas.toDataURL("image/png");

            // Mantenemos las proporciones dentro de los márgenes de 10mm
            const margin = 10;
            const usableWidth = pdfWidth - (margin * 2);
            const imgProps = pdf.getImageProperties(imgData);
            const usableHeight = (imgProps.height * usableWidth) / imgProps.width;

            let posicionYActual = margin;

            // Si el html2canvas es más largo que una hoja A4, es necesario cortar la imagen
            if (usableHeight > pdfHeight) {
                 // Por ahora ponemos la imagen única centrada y que sobresalga
                 // idealmente, podrías paginarla cortando canvas, pero por lo general
                 // la ficha debería ajustarse a una pantalla
                 pdf.addImage(imgData, "PNG", margin, posicionYActual, usableWidth, usableHeight);
            } else {
                 pdf.addImage(imgData, "PNG", margin, posicionYActual, usableWidth, usableHeight);
            }

            // Descargamos
            const nombreArchivo = `Ficha_Profesional_${cliente.apellido}_${cliente.nombre}.pdf`;
            pdf.save(nombreArchivo);

        } catch (error) {
            console.error("Error al exportar a PDF:", error);
            alert("Ocurrió un error al generar el PDF. Revisa la consola.");
        }
    };

    const handleImprimir = () => {
        const tieneAlgunaClave = cliente.clave?.claveAfip || cliente.clave?.claveSs || cliente.clave?.claveCidi;

        if (clavesExportar || !tieneAlgunaClave) {
            // Ya están desencriptadas, o no hay claves registradas en absoluto
            ejecutarGeneracionPDF();
        } else {
            // Pedimos contraseña para desencriptar antes de generar
            setDialogoSeguridad(true);
        }
    };

    const confirmarRevelarClaves = async () => {
        if (!passwordConfirm) {
            setErrorPassword('Ingrese su contraseña');
            return;
        }
        try {
            const data = await revelarClaves(cliente.cuil, passwordConfirm);
            setClavesExportar(data);
            setDialogoSeguridad(false);
            setPasswordConfirm('');
            setErrorPassword('');
            setGenerarPendiente(true);
        } catch (err) {
            console.error("Error al revelar:", err);
            const msg = err.response?.data?.message || err.response?.data || "Contraseña incorrecta";
            setErrorPassword(msg);
        }
    };

    const labelStyle = {
        backgroundColor: "#f8fafc",
        fontWeight: "700",
        fontSize: "0.7rem",
        color: "#64748b",
        textTransform: "uppercase",
        padding: "8px 12px",
        borderRight: "1px solid #e2e8f0",
        width: "38%",
    };

    const valueStyle = {
        padding: "8px 12px",
        fontSize: "0.85rem",
        color: "#1e293b",
        fontWeight: 500,
    };

    const sectionTitleStyle = {
        fontWeight: 900,
        color: primaryBlue,
        mb: 1.5,
        mt: 4,
        fontSize: "1rem",
        display: "flex",
        alignItems: "center",
        gap: 1,
        "&::after": {
            content: '""',
            flex: 1,
            height: "1px",
            backgroundColor: "#e2e8f0",
            ml: 2
        }
    };

    const RowInfo = ({ label, value }) => (
        <TableRow sx={{ borderBottom: "1px solid #e2e8f0" }}>
            <TableCell sx={labelStyle}>{label}</TableCell>
            <TableCell sx={valueStyle}>{value || "--"}</TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", py: { xs: 2, md: 5 } }}>
            {/* Panel de Control Flotante */}
            <Box sx={{
                maxWidth: "850px",
                margin: "0 auto 24px",
                px: 2,
                display: "flex",
                justifyContent: "space-between",
                "@media print": { display: "none !important" }
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onVolver}
                    variant="outlined"
                    sx={{ bgcolor: "white", borderRadius: "8px", textTransform: "none", borderColor: "#e2e8f0", color: "#64748b" }}
                >
                    Volver al perfil
                </Button>
                <Button
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleImprimir}
                    variant="contained"
                    sx={{ borderRadius: "8px", textTransform: "none", bgcolor: primaryBlue, boxShadow: "var(--shadow-md)", "&:hover": { bgcolor: "#2563eb" } }}
                >
                    Exportar a PDF
                </Button>
            </Box>

            <Paper
                id="vista-impresion"
                elevation={0}
                sx={{
                    p: { xs: 3, md: "50px" },
                    maxWidth: "850px",
                    margin: "0 auto",
                    backgroundColor: "white",
                    color: "black",
                    fontFamily: "'Public Sans', 'Segoe UI', sans-serif",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    "@media print": {
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        border: "none",
                        p: 0,
                        margin: 0,
                        boxShadow: "none"
                    }
                }}
            >
                {/* ENCABEZADO INSTITUCIONAL */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ p: 1, bgcolor: primaryBlue, borderRadius: "6px" }}>
                            <GavelIcon sx={{ color: "white", fontSize: 30 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: primaryBlue, lineHeight: 1, letterSpacing: "-0.5px" }}>
                                GESTIÓN EJP
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", fontSize: "0.6rem" }}>
                                Estudio Jurídico Previsional
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="right">
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.1rem" }}>FICHA RESUMEN CLIENTE</Typography>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, display: "block" }}>
                            Ref. {cliente.cuil ? cliente.cuil.toString().slice(-4) : "0000"}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2, borderColor: primaryBlue, borderBottomWidth: 2, opacity: 0.2 }} />

                {/* SECCIÓN 1: DATOS FILIATORIOS */}
                <Typography sx={sectionTitleStyle}>1. DATOS FILIATORIOS</Typography>
                <TableContainer component={Box} sx={{ border: "1px solid #e2e8f0", borderRadius: "4px" }}>
                    <Table size="small">
                        <TableBody>
                            <RowInfo label="Apellido y Nombre" value={`${cliente.apellido}, ${cliente.nombre}`} />
                            <RowInfo label="CUIL / CUIT" value={formatearCuil(cliente.cuil)} />
                            <RowInfo label="Fecha de Ingreso" value={formatearFecha(cliente.fechaIngreso)} />
                            <RowInfo label="Fecha de Nacimiento" value={formatearFecha(cliente.fechaNacimiento)} />
                            <RowInfo label="Edad Declarada" value={cliente.fechaNacimiento ? `${Math.floor((new Date() - new Date(cliente.fechaNacimiento)) / 31557600000)} años` : "--"} />
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* SECCIÓN 2: CONTACTO Y LOCALIZACIÓN */}
                <Typography sx={sectionTitleStyle}>2. CONTACTO Y LOCALIZACIÓN</Typography>
                <Grid container spacing={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "4px", overflow: "hidden", mb: 2 }}>
                    <Grid item xs={12} sm={6} sx={{ borderRight: { sm: "1px solid #e2e8f0" } }}>
                        <Table size="small">
                            <TableBody>
                                <RowInfo label="Teléfono propio" value={normalizeTelefonoForStorage(cliente.telefonoPropio)} />
                                <RowInfo label="Localidad" value={cliente.domicilio?.localidad?.nombre || cliente.domicilio?.localidad?.localidad} />
                                <RowInfo label="Calle / Altura" value={`${cliente.domicilio?.calle || ""} ${cliente.domicilio?.altura || ""}`} />
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Table size="small">
                            <TableBody>
                                <RowInfo label="Correo Electrónico" value={cliente.mail} />
                                <RowInfo label="Provincia" value={cliente.domicilio?.localidad?.provincia?.nombre} />
                                <RowInfo label="Barrio / Piso" value={`${cliente.domicilio?.barrio || ""} - ${cliente.domicilio?.piso || ""} ${cliente.domicilio?.departamento || ""}`} />
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>

                {/* NUEVO: CONTACTOS ADICIONALES (Integrado en el punto 2) */}
                {contactos && contactos.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, ml: 1, mb: 0.5, display: "block" }}>
                            CONTACTOS DE EMERGENCIA / ADICIONALES
                        </Typography>
                        <TableContainer component={Box} sx={{ border: "1px solid #e2e8f0", borderRadius: "4px" }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 900, py: 0.5 }}>NOMBRE</TableCell>
                                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 900, py: 0.5 }}>RELACIÓN</TableCell>
                                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 900, py: 0.5 }}>TELÉFONO</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contactos.map((contacto, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ fontSize: "0.8rem", py: 0.5 }}>{contacto.nombre || "--"}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", py: 0.5 }}>{contacto.relacion || "--"}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", py: 0.5 }}>{normalizeTelefonoForStorage(contacto.nroTelefono) || "--"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* SECCIÓN 3: INFORMACIÓN PREVISIONAL (Incluye Claves) */}
                                <Typography sx={sectionTitleStyle}>3. INFORMACIÓN PREVISIONAL</Typography>
                                <TableContainer component={Box} sx={{ border: "1px solid #e2e8f0", borderRadius: "4px", mb: 2 }}>
                                    <Table size="small">
                                        <TableBody>
                                            <RowInfo label="Oficio / Actividad" value={cliente.oficio} />
                                            <RowInfo label="Nivel Educativo" value={cliente.nivelEducativo?.nombre} />
                                            <RowInfo label="Referencia" value={cliente.referencia} />
                                            <TableRow>
                                                <TableCell sx={labelStyle}>Indicadores</TableCell>
                                                <TableCell sx={valueStyle}>
                                                    <Box display="flex" flexWrap="wrap" gap={3}>
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cliente.dosDoce ? "#10b981" : "#cbd5e1" }} />
                                                            <b>LEY 212:</b> {cliente.dosDoce ? "SÍ" : "NO"}
                                                        </Box>
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cliente.aportanteActual ? "#10b981" : "#cbd5e1" }} />
                                                            <b>APORTANTE:</b> {cliente.aportanteActual ? "SÍ" : "NO"}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Sub-bloque de Claves dentro de la Sección 3 */}
                                <Box sx={{ px: 1 }}>
                                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, mb: 1, display: "block", textTransform: "uppercase" }}>
                                        Claves de Acceso Fiscal / Previsional
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {['AFIP', 'SEG. SOCIAL', 'CIDI'].map((tipo, idx) => {
                                            const prop = ['claveAfip', 'claveSs', 'claveCidi'][idx];

                                            // ¿Está vacío en la base de datos?
                                            const estaVacio = !cliente.clave || !cliente.clave[prop] || cliente.clave[prop].trim() === '';

                                            let textoClave = "-";
                                            if (clavesExportar && clavesExportar[prop]) {
                                                textoClave = clavesExportar[prop];
                                            } else if (!estaVacio) {
                                                textoClave = cliente.clave[prop];
                                            }

                                            return (
                                                <Grid item xs={4} key={tipo}>
                                                    <Box sx={{
                                                        p: 1.2,
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: "6px",
                                                        textAlign: "center",
                                                        bgcolor: "#fcfcfc"
                                                    }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: "#94a3b8", display: "block", mb: 0.3, fontSize: "0.6rem" }}>{tipo}</Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: "'Courier New', monospace", fontWeight: "bold", color: (textoClave === "-") ? "#cbd5e1" : "#1e293b", fontSize: "0.8rem" }}>
                                                            {textoClave}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>

                {/* SECCIÓN 5: OBSERVACIONES */}
                <Typography sx={sectionTitleStyle}>5. OBSERVACIONES Y NOTAS</Typography>
                <Box sx={{
                    p: 2,
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    bgcolor: "#fffcf0",
                    minHeight: "100px",
                    fontSize: "0.85rem",
                    lineHeight: 1.6,
                    color: "#475569",
                    whiteSpace: "pre-wrap"
                }}>
                    {cliente.observacion || "Sin novedades registradas."}
                </Box>

                {/* PIE DE PÁGINA */}
                <Box sx={{ mt: 6, pt: 2, borderTop: "1px solid #f1f5f9", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: "0.65rem" }}>
                        Generado el {new Date().toLocaleDateString('es-AR')} a las {new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                </Box>
            </Paper>

            {/* --- ESTILOS PARA IMPRESIÓN --- */}
            <style>{`
            @media print {
                @page {
                    size: A4;
                    margin: 1cm;
                }

                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }

                #print-container-temp {
                    background-color: white !important;
                    color: black !important;
                    width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
            }
            `}</style>

            {/* --- DIÁLOGO DE SEGURIDAD PARA EXPORTAR --- */}
            <Dialog open={dialogoSeguridad} onClose={() => setDialogoSeguridad(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon color="warning" />
                    Seguridad Requerida para PDF
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Para que las claves fiscales de este cliente aparezcan legibles en el PDF, por favor confirme su contraseña.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="password-pdf"
                        label="Contraseña"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        error={!!errorPassword}
                        helperText={errorPassword}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') confirmarRevelarClaves();
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogoSeguridad(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={confirmarRevelarClaves} variant="contained" color="primary">
                        Confirmar y Exportar
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
export default ClienteImpresion;
